/**
 * cave_light — THE BAKED CAVE LIGHT FIELD (shader gaps program, agent A, 2026-09-29). PURE: no three.js, no DOM —
 * the cave worker runs the bake, the harness (`verify:cave-light`) pins the math, the main thread samples it for the
 * lighting hook, and `cave_light_material.ts` samples the SAME layout on the GPU.
 *
 * What it answers, per probe of a coarse grid over one cave (Majercik et al. 2019 "Dynamic Diffuse Global
 * Illumination with Ray-Traced Irradiance Fields", JCGT 8(2) — a grid of irradiance probes, rays per probe, probes
 * that land in rock relocated or switched off; RTXGI's DDGIVolume: relocation ≤ 45 % of a cell, a probe whose
 * rays hit > 25 % BACK faces is inside geometry and inactive):
 *
 *   T  — SKY TRANSFER: the fraction of a unit, uniform sky's irradiance that reaches the probe, cosine-weighted per
 *        basis direction, INCLUDING one bounce off the rock (Unity's Adaptive Probe Volume "sky occlusion": a static
 *        occlusion value per probe, "including light that bounced off static GameObjects", combined at runtime with
 *        the LIVE sky colour — so time of day and weather stay live; only the geometry is baked).
 *   E  — LOCAL IRRADIANCE (rgb, the crystals' own scene units): the crystals' emission reaching the probe, plus one
 *        bounce of it off the rock.
 *
 * THE BASIS IS VALVE'S AMBIENT CUBE (Mitchell, McTaggart & Green, "Shading in Valve's Source Engine", SIGGRAPH 2006
 * course): six axis values, evaluated `Σ n_i² · C[sign(n_i)]` — three non-zero terms per normal, never negative
 * (L1 spherical harmonics ring below zero, and a NEGATIVE visibility is nonsense), a closer fit than the first two SH
 * bands for the same budget (Ramamoorthi & Hanrahan 2001 need nine coefficients for 1 % error on irradiance). Each
 * face stores the irradiance for a surface whose normal IS that axis: C[a] = ∫ L(ω) max(0, ω·a) dω.
 *
 * Face order +x, −x, +y, −y, +z, −z (the engine ProbeVolume's). The field is FACE-MAJOR: face f's probes are
 * contiguous (one Data3DTexture per face), probe index ((iz·ny)+iy)·nx+ix, four floats per probe (E.rgb, T).
 * Positions are the cave's MOUTH-RELATIVE frame (the cave group's local space).
 */

export const CAVE_LIGHT_FIELD_VERSION = 1;
/** Face axes in storage order. */
export const CUBE_AXES: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];
/** DDGI relocation reach: a probe moves at most this fraction of a cell (RTXGI: "45 % of the grid cell distance"). */
export const RELOCATE_MAX = 0.45;
/** DDGI classification: more than this share of back-face hits means the probe sits inside geometry. */
export const BACKFACE_KILL = 0.25;
/** The GPU and CPU samplers push the lookup this fraction of a cell along the surface normal (DDGI's normal bias). */
export const NORMAL_BIAS = 0.45;

export type Vec3 = readonly [number, number, number];

export interface CaveLightEmitter { x: number; y: number; z: number; /** effective radius (m) */ r: number; /** radiance (scene units) */ rad: Vec3 }

export interface CaveLightBakeInput {
  /** Occluder triangles (mouth-relative); the winding's normal faces the AIR. */
  positions: Float32Array;
  indices: Uint32Array;
  /** Per triangle: 0 = cave rock, 1 = open ground (the terrain around the mouth). Absent = all rock. */
  triKind?: Uint8Array;
  /** The probe grid's box (mouth-relative). */
  box: { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number };
  /** Target probe spacing (m); grown when the box would need more than `maxProbes`. */
  spacing: number;
  maxProbes?: number;
  /** Signed field: < 0 in the AIR (cavity or open sky), ≥ 0 in rock. A distance bound (used for relocation). */
  air: (x: number, y: number, z: number) => number;
  /** Signed distance to the CAVITY air alone (< 0 inside it). Probes within `traceBand` of it are traced; farther
   *  air probes are OPEN sky (T = 1 on every face, E = 0) — the outcrop's outer skin keeps its outdoor look. */
  cavity?: (x: number, y: number, z: number) => number;
  /** Height of the ground beyond the triangle domain — escaped rays that dip under it saw ground, not sky. */
  farGround?: (x: number, z: number) => number;
  emitters?: CaveLightEmitter[];
  rockAlbedo?: number;
  groundAlbedo?: number;
  rays?: number;
  seed?: number;
  /** One bounce (pass 2): rock hits re-radiate the first pass. Default true. */
  bounce?: boolean;
  /** Cells of the occluder grid (m). Default 1.6. */
  cellSize?: number;
}

export const ProbeState = { Inactive: 0, Traced: 1, Relocated: 2, Open: 3 } as const;
export type ProbeState = typeof ProbeState[keyof typeof ProbeState];

export interface CaveLightField {
  version: number;
  /** Position of probe (0,0,0) — mouth-relative. */
  origin: [number, number, number];
  spacing: number;
  dims: [number, number, number];
  /** FACE-MAJOR: [face][probe][E.r, E.g, E.b, T]. */
  data: Float32Array;
  state: Uint8Array;
  stats: CaveLightStats;
}
export interface CaveLightStats {
  probes: number; traced: number; relocated: number; open: number; inactive: number; backfaceKilled: number;
  rays: number; escaped: number; emitters: number; ms: number; triangles: number;
}

// ── small maths ──────────────────────────────────────────────────────────────────────────────────────────────────────

function hash32(a: number, b: number): number {
  let h = (Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1)) | 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}
const h01 = (a: number, b: number): number => hash32(a, b) / 4294967296;

/** N directions evenly spread on the sphere (the Fibonacci lattice): deterministic, no clumps. */
export function fibonacciSphere(n: number): Float32Array {
  const out = new Float32Array(n * 3);
  const ga = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (2 * (i + 0.5)) / n;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const a = ga * i;
    out[i * 3] = Math.cos(a) * r; out[i * 3 + 1] = y; out[i * 3 + 2] = Math.sin(a) * r;
  }
  return out;
}

/** A rotation matrix (row-major 3×3) from two hashed numbers — each probe turns the lattice its own way (DDGI's
 *  per-update random rotation, fixed per probe here so the bake is deterministic). */
function rotationFor(i: number, seed: number, out: Float64Array): void {
  const u1 = h01(i, seed), u2 = h01(i, seed ^ 0x9e3779b9), u3 = h01(i ^ 0x5bd1e995, seed);
  // Shoemake's uniform random quaternion
  const s1 = Math.sqrt(1 - u1), s2 = Math.sqrt(u1);
  const qx = s1 * Math.sin(2 * Math.PI * u2), qy = s1 * Math.cos(2 * Math.PI * u2);
  const qz = s2 * Math.sin(2 * Math.PI * u3), qw = s2 * Math.cos(2 * Math.PI * u3);
  out[0] = 1 - 2 * (qy * qy + qz * qz); out[1] = 2 * (qx * qy - qz * qw); out[2] = 2 * (qx * qz + qy * qw);
  out[3] = 2 * (qx * qy + qz * qw); out[4] = 1 - 2 * (qx * qx + qz * qz); out[5] = 2 * (qy * qz - qx * qw);
  out[6] = 2 * (qx * qz - qy * qw); out[7] = 2 * (qy * qz + qx * qw); out[8] = 1 - 2 * (qx * qx + qy * qy);
}

/** Ambient-cube weights of a direction (face order +x −x +y −y +z −z): max(0, ω·a)². Sums to 1 for a unit ω. */
export function cubeWeights(nx: number, ny: number, nz: number, out: number[] = [0, 0, 0, 0, 0, 0]): number[] {
  out[0] = nx > 0 ? nx * nx : 0; out[1] = nx < 0 ? nx * nx : 0;
  out[2] = ny > 0 ? ny * ny : 0; out[3] = ny < 0 ? ny * ny : 0;
  out[4] = nz > 0 ? nz * nz : 0; out[5] = nz < 0 ? nz * nz : 0;
  return out;
}

/** Evaluate an ambient cube (six values) for a unit normal — Valve's routine. */
export function evalCube(c: ArrayLike<number>, nx: number, ny: number, nz: number): number {
  return (nx >= 0 ? c[0] : c[1]) * nx * nx + (ny >= 0 ? c[2] : c[3]) * ny * ny + (nz >= 0 ? c[4] : c[5]) * nz * nz;
}

/** IEEE half from a float (round to nearest; the GPU field is RGBA16F). */
const HALF_F32 = new Float32Array(1), HALF_U32 = new Uint32Array(HALF_F32.buffer);
export function toHalf(v: number): number {
  HALF_F32[0] = v;
  const x = HALF_U32[0];
  const sign = (x >>> 16) & 0x8000;
  let e = ((x >>> 23) & 0xff) - 127 + 15;
  let m = x & 0x7fffff;
  if (((x >>> 23) & 0xff) === 0xff) return sign | 0x7c00 | (m ? 0x200 : 0);
  if (e >= 0x1f) return sign | 0x7c00;
  if (e <= 0) {
    if (e < -10) return sign;
    m = (m | 0x800000) >> (1 - e);
    return sign | ((m + 0x1000) >> 13);
  }
  const r = sign | (e << 10) | (m >> 13);
  return (m & 0x1000) ? r + 1 : r;
}
let HALF_LUT: Float32Array | null = null;
/** A half float's value (a 64 k table, built on first use). */
export function fromHalf(h: number): number {
  if (!HALF_LUT) {
    HALF_LUT = new Float32Array(65536);
    for (let i = 0; i < 65536; i++) {
      const s = i & 0x8000 ? -1 : 1, e = (i >> 10) & 0x1f, m = i & 0x3ff;
      HALF_LUT[i] = e === 0 ? s * m * 2 ** -24 : e === 31 ? (m ? NaN : s * Infinity) : s * (1 + m / 1024) * 2 ** (e - 15);
    }
  }
  return HALF_LUT[h & 0xffff];
}
/** A field whose data is the GPU's half floats (the main thread keeps only these — the hook samples them). */
export function halfFieldView(f: { origin: [number, number, number]; spacing: number; dims: [number, number, number]; half: Uint16Array }): Pick<CaveLightField, 'origin' | 'spacing' | 'dims' | 'data'> {
  const half = f.half;
  const data = new Proxy({} as Float32Array, { get: (_t, k) => (typeof k === 'string' && k !== 'length' ? fromHalf(half[+k]) : k === 'length' ? half.length : undefined) });
  return { origin: f.origin, spacing: f.spacing, dims: f.dims, data };
}
/** The field's data as six half-float faces (one RGBA16F Data3DTexture each). */
export function fieldToHalf(field: CaveLightField): Uint16Array {
  const out = new Uint16Array(field.data.length);
  for (let i = 0; i < out.length; i++) out[i] = toHalf(field.data[i]);
  return out;
}

// ── the occluder grid (a uniform grid + 3D-DDA; Amanatides & Woo 1987) ──────────────────────────────────────────────

export class TriGrid {
  readonly x0: number; readonly y0: number; readonly z0: number;
  readonly cs: number; readonly nx: number; readonly ny: number; readonly nz: number;
  readonly x1: number; readonly y1: number; readonly z1: number;
  private readonly start: Int32Array;
  private readonly items: Int32Array;
  /** per triangle: v0(3) e1(3) e2(3) n(3, unit, from the winding) */
  readonly tri: Float32Array;
  readonly kind: Uint8Array;
  readonly count: number;
  private readonly mail: Int32Array;
  private rayId = 0;
  hitT = 0;
  hitTri = -1;

  constructor(positions: Float32Array, indices: Uint32Array, triKind: Uint8Array | undefined, cellSize: number) {
    const T = indices.length / 3;
    this.count = T;
    this.tri = new Float32Array(T * 12);
    this.kind = triKind ?? new Uint8Array(T);
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let t = 0; t < T; t++) {
      const a = indices[t * 3] * 3, b = indices[t * 3 + 1] * 3, c = indices[t * 3 + 2] * 3;
      const ax = positions[a], ay = positions[a + 1], az = positions[a + 2];
      const e1x = positions[b] - ax, e1y = positions[b + 1] - ay, e1z = positions[b + 2] - az;
      const e2x = positions[c] - ax, e2y = positions[c + 1] - ay, e2z = positions[c + 2] - az;
      let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
      const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;
      const o = t * 12;
      this.tri[o] = ax; this.tri[o + 1] = ay; this.tri[o + 2] = az;
      this.tri[o + 3] = e1x; this.tri[o + 4] = e1y; this.tri[o + 5] = e1z;
      this.tri[o + 6] = e2x; this.tri[o + 7] = e2y; this.tri[o + 8] = e2z;
      this.tri[o + 9] = nx; this.tri[o + 10] = ny; this.tri[o + 11] = nz;
      for (const k of [a, b, c]) {
        const px = positions[k], py = positions[k + 1], pz = positions[k + 2];
        if (px < x0) x0 = px; if (py < y0) y0 = py; if (pz < z0) z0 = pz;
        if (px > x1) x1 = px; if (py > y1) y1 = py; if (pz > z1) z1 = pz;
      }
    }
    if (T === 0) { x0 = y0 = z0 = 0; x1 = y1 = z1 = 1; }
    const pad = 0.01;
    this.cs = cellSize;
    this.x0 = x0 - pad; this.y0 = y0 - pad; this.z0 = z0 - pad;
    this.nx = Math.max(1, Math.ceil((x1 + pad - this.x0) / cellSize));
    this.ny = Math.max(1, Math.ceil((y1 + pad - this.y0) / cellSize));
    this.nz = Math.max(1, Math.ceil((z1 + pad - this.z0) / cellSize));
    this.x1 = this.x0 + this.nx * cellSize; this.y1 = this.y0 + this.ny * cellSize; this.z1 = this.z0 + this.nz * cellSize;
    const cells = this.nx * this.ny * this.nz;
    const counts = new Int32Array(cells + 1);
    const span = (t: number, cb: (cell: number) => void): void => {
      const o = t * 12;
      const ax = this.tri[o], ay = this.tri[o + 1], az = this.tri[o + 2];
      const bx = ax + this.tri[o + 3], by = ay + this.tri[o + 4], bz = az + this.tri[o + 5];
      const cx = ax + this.tri[o + 6], cy = ay + this.tri[o + 7], cz = az + this.tri[o + 8];
      const i0 = this.cellX(Math.min(ax, bx, cx)), i1 = this.cellX(Math.max(ax, bx, cx));
      const j0 = this.cellY(Math.min(ay, by, cy)), j1 = this.cellY(Math.max(ay, by, cy));
      const k0 = this.cellZ(Math.min(az, bz, cz)), k1 = this.cellZ(Math.max(az, bz, cz));
      for (let k = k0; k <= k1; k++) for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) cb((k * this.ny + j) * this.nx + i);
    };
    for (let t = 0; t < T; t++) span(t, (c) => { counts[c + 1]++; });
    for (let c = 0; c < cells; c++) counts[c + 1] += counts[c];
    this.start = counts;
    this.items = new Int32Array(counts[cells]);
    const fill = counts.slice(0, cells);
    for (let t = 0; t < T; t++) span(t, (c) => { this.items[fill[c]++] = t; });
    this.mail = new Int32Array(T).fill(-1);
  }

  private cellX(x: number): number { return Math.min(this.nx - 1, Math.max(0, Math.floor((x - this.x0) / this.cs))); }
  private cellY(y: number): number { return Math.min(this.ny - 1, Math.max(0, Math.floor((y - this.y0) / this.cs))); }
  private cellZ(z: number): number { return Math.min(this.nz - 1, Math.max(0, Math.floor((z - this.z0) / this.cs))); }

  /**
   * The nearest triangle along o + t·d for t in (tMin, tMax]; double-sided. Returns false when nothing is hit inside
   * the grid. `hitT` / `hitTri` carry the answer. d must be unit length.
   */
  trace(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, tMin: number, tMax: number): boolean {
    this.hitTri = -1;
    this.hitT = tMax;
    if (this.count === 0) return false;
    const id = ++this.rayId;
    // clip the ray to the grid box (slabs)
    const idx = 1 / (Math.abs(dx) < 1e-12 ? (dx < 0 ? -1e-12 : 1e-12) : dx);
    const idy = 1 / (Math.abs(dy) < 1e-12 ? (dy < 0 ? -1e-12 : 1e-12) : dy);
    const idz = 1 / (Math.abs(dz) < 1e-12 ? (dz < 0 ? -1e-12 : 1e-12) : dz);
    let tx0 = (this.x0 - ox) * idx, tx1 = (this.x1 - ox) * idx; if (tx0 > tx1) { const s = tx0; tx0 = tx1; tx1 = s; }
    let ty0 = (this.y0 - oy) * idy, ty1 = (this.y1 - oy) * idy; if (ty0 > ty1) { const s = ty0; ty0 = ty1; ty1 = s; }
    let tz0 = (this.z0 - oz) * idz, tz1 = (this.z1 - oz) * idz; if (tz0 > tz1) { const s = tz0; tz0 = tz1; tz1 = s; }
    const tEnter = Math.max(tMin, tx0, ty0, tz0);
    const tExit = Math.min(tMax, tx1, ty1, tz1);
    if (tEnter > tExit) return false;
    const px = ox + dx * tEnter, py = oy + dy * tEnter, pz = oz + dz * tEnter;
    let i = this.cellX(px), j = this.cellY(py), k = this.cellZ(pz);
    const si = dx > 0 ? 1 : -1, sj = dy > 0 ? 1 : -1, sk = dz > 0 ? 1 : -1;
    const cs = this.cs;
    let tmx = dx !== 0 ? (this.x0 + (i + (si > 0 ? 1 : 0)) * cs - ox) * idx : Infinity;
    let tmy = dy !== 0 ? (this.y0 + (j + (sj > 0 ? 1 : 0)) * cs - oy) * idy : Infinity;
    let tmz = dz !== 0 ? (this.z0 + (k + (sk > 0 ? 1 : 0)) * cs - oz) * idz : Infinity;
    const tdx = dx !== 0 ? Math.abs(cs * idx) : Infinity, tdy = dy !== 0 ? Math.abs(cs * idy) : Infinity, tdz = dz !== 0 ? Math.abs(cs * idz) : Infinity;
    const tri = this.tri;
    let best = tMax, bestTri = -1;
    for (;;) {
      const c = (k * this.ny + j) * this.nx + i;
      for (let p = this.start[c], e = this.start[c + 1]; p < e; p++) {
        const t = this.items[p];
        if (this.mail[t] === id) continue;
        this.mail[t] = id;
        const o = t * 12;
        const e1x = tri[o + 3], e1y = tri[o + 4], e1z = tri[o + 5];
        const e2x = tri[o + 6], e2y = tri[o + 7], e2z = tri[o + 8];
        const pvx = dy * e2z - dz * e2y, pvy = dz * e2x - dx * e2z, pvz = dx * e2y - dy * e2x;
        const det = e1x * pvx + e1y * pvy + e1z * pvz;
        if (det > -1e-12 && det < 1e-12) continue;
        const inv = 1 / det;
        const tvx = ox - tri[o], tvy = oy - tri[o + 1], tvz = oz - tri[o + 2];
        const u = (tvx * pvx + tvy * pvy + tvz * pvz) * inv;
        if (u < 0 || u > 1) continue;
        const qx = tvy * e1z - tvz * e1y, qy = tvz * e1x - tvx * e1z, qz = tvx * e1y - tvy * e1x;
        const v = (dx * qx + dy * qy + dz * qz) * inv;
        if (v < 0 || u + v > 1) continue;
        const tt = (e2x * qx + e2y * qy + e2z * qz) * inv;
        if (tt > tMin && tt < best) { best = tt; bestTri = t; }
      }
      const tCellExit = Math.min(tmx, tmy, tmz);
      if (bestTri >= 0 && best <= tCellExit) break;
      if (tCellExit > tExit) break;
      if (tmx <= tmy && tmx <= tmz) { i += si; tmx += tdx; if (i < 0 || i >= this.nx) break; }
      else if (tmy <= tmz) { j += sj; tmy += tdy; if (j < 0 || j >= this.ny) break; }
      else { k += sk; tmz += tdz; if (k < 0 || k >= this.nz) break; }
    }
    if (bestTri < 0) return false;
    this.hitT = best; this.hitTri = bestTri;
    return true;
  }
}

// ── the field: layout + sampling ─────────────────────────────────────────────────────────────────────────────────────

export function probeCount(f: { dims: readonly number[] }): number { return f.dims[0] * f.dims[1] * f.dims[2]; }

/**
 * Trilinear sample of the field at a mouth-relative point (texel-centre convention, clamped to the edge — the GPU's
 * Data3DTexture with ClampToEdge + LinearFilter), evaluated for normal n. `bias` pushes the lookup along n by that
 * fraction of a cell. Returns [E.r, E.g, E.b, T].
 */
export function sampleField(
  f: Pick<CaveLightField, 'origin' | 'spacing' | 'dims' | 'data'>,
  x: number, y: number, z: number, nx: number, ny: number, nz: number, bias = NORMAL_BIAS,
  out: number[] = [0, 0, 0, 0],
): number[] {
  const [dx, dy, dz] = f.dims;
  const P = dx * dy * dz;
  const b = bias * f.spacing;
  const gx = (x + nx * b - f.origin[0]) / f.spacing, gy = (y + ny * b - f.origin[1]) / f.spacing, gz = (z + nz * b - f.origin[2]) / f.spacing;
  const cx = Math.min(dx - 1, Math.max(0, gx)), cy = Math.min(dy - 1, Math.max(0, gy)), cz = Math.min(dz - 1, Math.max(0, gz));
  const i0 = Math.min(dx - 2 < 0 ? 0 : dx - 2, Math.floor(cx)), j0 = Math.min(dy - 2 < 0 ? 0 : dy - 2, Math.floor(cy)), k0 = Math.min(dz - 2 < 0 ? 0 : dz - 2, Math.floor(cz));
  const fx = dx > 1 ? cx - i0 : 0, fy = dy > 1 ? cy - j0 : 0, fz = dz > 1 ? cz - k0 : 0;
  const i1 = Math.min(dx - 1, i0 + 1), j1 = Math.min(dy - 1, j0 + 1), k1 = Math.min(dz - 1, k0 + 1);
  const w = cubeWeights(nx, ny, nz);
  out[0] = out[1] = out[2] = out[3] = 0;
  for (let face = 0; face < 6; face++) {
    const wf = w[face];
    if (wf === 0) continue;
    const base = face * P;
    for (let c = 0; c < 4; c++) {
      const at = (i: number, j: number, k: number): number => f.data[(base + (k * dy + j) * dx + i) * 4 + c];
      const c00 = at(i0, j0, k0) * (1 - fx) + at(i1, j0, k0) * fx;
      const c10 = at(i0, j1, k0) * (1 - fx) + at(i1, j1, k0) * fx;
      const c01 = at(i0, j0, k1) * (1 - fx) + at(i1, j0, k1) * fx;
      const c11 = at(i0, j1, k1) * (1 - fx) + at(i1, j1, k1) * fx;
      const v = (c00 * (1 - fy) + c10 * fy) * (1 - fz) + (c01 * (1 - fy) + c11 * fy) * fz;
      out[c] += v * wf;
    }
  }
  return out;
}

/**
 * THE HOOK'S TARGET at a point (an actor, not a surface): the mean over the six faces — a body facing every way.
 * `mul` = the sky transfer the global hemisphere + IBL take; `tint` = the local irradiance added as a flat fill.
 */
export function hookTargetAt(f: Pick<CaveLightField, 'origin' | 'spacing' | 'dims' | 'data'>, x: number, y: number, z: number): { mul: number; tint: [number, number, number] } {
  let T = 0, r = 0, g = 0, b = 0;
  const s = [0, 0, 0, 0];
  for (const [ax, ay, az] of CUBE_AXES) {
    sampleField(f, x, y, z, ax, ay, az, 0, s);
    r += s[0]; g += s[1]; b += s[2]; T += s[3];
  }
  return { mul: Math.min(1, Math.max(0, T / 6)), tint: [r / 6, g / 6, b / 6] };
}

// ── the bake ─────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Inactive probes take the mean of their active 6-neighbours, a few rings deep (lightmap-style dilation), so a
 *  lookup whose trilinear footprint touches rock reads its air neighbours instead of zero. */
function dilate(data: Float32Array, state: Uint8Array, dims: readonly number[], passes: number): void {
  const [nx, ny, nz] = dims;
  const P = nx * ny * nz;
  let filled = new Uint8Array(P);
  for (let p = 0; p < P; p++) filled[p] = state[p] !== ProbeState.Inactive ? 1 : 0;
  const acc = new Float64Array(24);
  for (let pass = 0; pass < passes; pass++) {
    const next = filled.slice();
    let any = false;
    for (let k = 0; k < nz; k++) for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
      const p = (k * ny + j) * nx + i;
      if (filled[p]) continue;
      acc.fill(0);
      let n = 0;
      const take = (q: number): void => {
        if (!filled[q]) return;
        n++;
        for (let face = 0; face < 6; face++) for (let c = 0; c < 4; c++) acc[face * 4 + c] += data[(face * P + q) * 4 + c];
      };
      if (i > 0) take(p - 1); if (i < nx - 1) take(p + 1);
      if (j > 0) take(p - nx); if (j < ny - 1) take(p + nx);
      if (k > 0) take(p - nx * ny); if (k < nz - 1) take(p + nx * ny);
      if (n === 0) continue;
      for (let face = 0; face < 6; face++) for (let c = 0; c < 4; c++) data[(face * P + p) * 4 + c] = acc[face * 4 + c] / n;
      next[p] = 1; any = true;
    }
    filled = next;
    if (!any) break;
  }
}

const RAY_ESCAPE = 2, RAY_GROUND = 1, RAY_ROCK = 0;

/** Bake the field. Deterministic for identical input (no Math.random; per-probe hashed ray rotations). */
export function bakeCaveLight(inp: CaveLightBakeInput): CaveLightField {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const rays = Math.max(8, inp.rays ?? 64);
  const seed = (inp.seed ?? 1) | 0;
  const rockAlbedo = inp.rockAlbedo ?? 0.18;
  const groundAlbedo = inp.groundAlbedo ?? 0.22;
  const emitters = inp.emitters ?? [];
  const bounce = inp.bounce !== false;
  const box = inp.box;
  const maxProbes = inp.maxProbes ?? 60000;
  let spacing = inp.spacing;
  const ext = [Math.max(0, box.x1 - box.x0), Math.max(0, box.y1 - box.y0), Math.max(0, box.z1 - box.z0)];
  let dims: [number, number, number];
  for (;;) {
    // (dims − 1)·spacing ≥ the box on every axis: the lattice covers it; a zero-extent axis is one probe
    dims = [Math.ceil(ext[0] / spacing - 1e-9) + 1, Math.ceil(ext[1] / spacing - 1e-9) + 1, Math.ceil(ext[2] / spacing - 1e-9) + 1];
    if (dims[0] * dims[1] * dims[2] <= maxProbes) break;
    spacing *= 1.12;
  }
  const [nx, ny, nz] = dims;
  const P = nx * ny * nz;
  // centre the lattice on the box
  const origin: [number, number, number] = [
    (box.x0 + box.x1) / 2 - ((nx - 1) * spacing) / 2,
    (box.y0 + box.y1) / 2 - ((ny - 1) * spacing) / 2,
    (box.z0 + box.z1) / 2 - ((nz - 1) * spacing) / 2,
  ];
  const data = new Float32Array(P * 6 * 4);
  const state = new Uint8Array(P);
  const pos = new Float32Array(P * 3);
  const cavity = inp.cavity ?? inp.air;
  const traceBand = 2.2 * spacing;
  const stats: CaveLightStats = { probes: P, traced: 0, relocated: 0, open: 0, inactive: 0, backfaceKilled: 0, rays: 0, escaped: 0, emitters: emitters.length, ms: 0, triangles: inp.indices.length / 3 };

  // ── 1. classify + relocate (DDGI) ──
  const reach = RELOCATE_MAX * spacing;
  for (let k = 0; k < nz; k++) for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    const p = (k * ny + j) * nx + i;
    let x = origin[0] + i * spacing, y = origin[1] + j * spacing, z = origin[2] + k * spacing;
    const a = inp.air(x, y, z);
    let st: ProbeState = ProbeState.Inactive;
    if (a < 0) st = cavity(x, y, z) < traceBand ? ProbeState.Traced : ProbeState.Open;
    else if (a < reach) {
      // one Newton step down the field's gradient into the air, capped at RELOCATE_MAX of a cell
      const h = 0.1 * spacing;
      let gx = inp.air(x + h, y, z) - inp.air(x - h, y, z);
      let gy = inp.air(x, y + h, z) - inp.air(x, y - h, z);
      let gz = inp.air(x, y, z + h) - inp.air(x, y, z - h);
      const gl = Math.hypot(gx, gy, gz);
      if (gl > 1e-9) {
        gx /= gl; gy /= gl; gz /= gl;
        const step = Math.min(reach, a + 0.12 * spacing);
        const rx = x - gx * step, ry = y - gy * step, rz = z - gz * step;
        if (inp.air(rx, ry, rz) < 0) {
          x = rx; y = ry; z = rz;
          st = cavity(x, y, z) < traceBand ? ProbeState.Relocated : ProbeState.Open;
          if (st === ProbeState.Relocated) stats.relocated++;
        }
      }
    }
    state[p] = st;
    pos[p * 3] = x; pos[p * 3 + 1] = y; pos[p * 3 + 2] = z;
  }

  // ── 2. trace (pass 1: direct sky transfer + the emitters) ──
  const grid = new TriGrid(inp.positions, inp.indices, inp.triKind, inp.cellSize ?? 1.6);
  const dirs = fibonacciSphere(rays);
  const tracedList: number[] = [];
  for (let p = 0; p < P; p++) if (state[p] === ProbeState.Traced || state[p] === ProbeState.Relocated) tracedList.push(p);
  const TR = tracedList.length;
  // per ray: kind (0 rock, 1 ground, 2 escape) and for rock the hit point + normal facing the probe
  const rayKind = new Uint8Array(TR * rays);
  const rayHit = new Float32Array(TR * rays * 6);
  const rayDir = new Float32Array(TR * rays * 3);
  const R = new Float64Array(9);
  const farMax = 260, farStep = 6;
  const wT = 4 / rays, wE = (4 * Math.PI) / rays;
  for (let ti = 0; ti < TR; ti++) {
    const p = tracedList[ti];
    const ox = pos[p * 3], oy = pos[p * 3 + 1], oz = pos[p * 3 + 2];
    rotationFor(p, seed, R);
    let back = 0;
    const acc = [0, 0, 0, 0, 0, 0];
    for (let r = 0; r < rays; r++) {
      const fx = dirs[r * 3], fy = dirs[r * 3 + 1], fz = dirs[r * 3 + 2];
      const dx = R[0] * fx + R[1] * fy + R[2] * fz, dy = R[3] * fx + R[4] * fy + R[5] * fz, dz = R[6] * fx + R[7] * fy + R[8] * fz;
      const ri = ti * rays + r;
      rayDir[ri * 3] = dx; rayDir[ri * 3 + 1] = dy; rayDir[ri * 3 + 2] = dz;
      stats.rays++;
      let kind = RAY_ESCAPE;
      if (grid.trace(ox, oy, oz, dx, dy, dz, 1e-3, 1e9)) {
        const t = grid.hitTri, o = t * 12;
        const tnx = grid.tri[o + 9], tny = grid.tri[o + 10], tnz = grid.tri[o + 11];
        const facing = tnx * dx + tny * dy + tnz * dz;
        if (facing > 0) back++;
        kind = grid.kind[t] === 1 ? RAY_GROUND : RAY_ROCK;
        if (kind === RAY_ROCK) {
          const s = facing > 0 ? 1 : -1; // the normal facing the probe
          const hx = ox + dx * grid.hitT, hy = oy + dy * grid.hitT, hz = oz + dz * grid.hitT;
          rayHit[ri * 6] = hx; rayHit[ri * 6 + 1] = hy; rayHit[ri * 6 + 2] = hz;
          rayHit[ri * 6 + 3] = -s * tnx; rayHit[ri * 6 + 4] = -s * tny; rayHit[ri * 6 + 5] = -s * tnz;
        }
      } else if (inp.farGround && dy < 0.7) {
        // left the occluder domain: the hills beyond it (a coarse march)
        let t = Math.max(0, exitT(grid, ox, oy, oz, dx, dy, dz));
        for (; t < farMax; t += farStep) {
          const x = ox + dx * t, y = oy + dy * t, z = oz + dz * t;
          if (y < inp.farGround(x, z)) { kind = RAY_GROUND; break; }
        }
      }
      rayKind[ri] = kind;
      if (kind === RAY_ESCAPE) stats.escaped++;
      const L = kind === RAY_ESCAPE ? 1 : kind === RAY_GROUND ? groundAlbedo : 0;
      if (L > 0) {
        if (dx > 0) acc[0] += L * dx; else acc[1] -= L * dx;
        if (dy > 0) acc[2] += L * dy; else acc[3] -= L * dy;
        if (dz > 0) acc[4] += L * dz; else acc[5] -= L * dz;
      }
    }
    if (back > BACKFACE_KILL * rays) { state[p] = ProbeState.Inactive; stats.backfaceKilled++; continue; }
    for (let face = 0; face < 6; face++) data[(face * P + p) * 4 + 3] = acc[face] * wT;
    // the emitters: an analytic small sphere each, one shadow ray
    for (const e of emitters) {
      const vx = e.x - ox, vy = e.y - oy, vz = e.z - oz;
      const d = Math.hypot(vx, vy, vz);
      if (d < 1e-4 || d > 60) continue;
      const ux = vx / d, uy = vy / d, uz = vz / d;
      const stop = d - e.r * 1.1;
      if (stop > 1e-3 && grid.trace(ox, oy, oz, ux, uy, uz, 1e-3, stop)) continue;
      const dc = Math.max(d, e.r * 1.05);
      const s = e.r / dc;
      const omega = 2 * Math.PI * (1 - Math.sqrt(Math.max(0, 1 - s * s)));
      const w6 = [ux > 0 ? ux : 0, ux < 0 ? -ux : 0, uy > 0 ? uy : 0, uy < 0 ? -uy : 0, uz > 0 ? uz : 0, uz < 0 ? -uz : 0];
      for (let face = 0; face < 6; face++) {
        if (w6[face] <= 0) continue;
        const o = (face * P + p) * 4;
        data[o] += e.rad[0] * omega * w6[face]; data[o + 1] += e.rad[1] * omega * w6[face]; data[o + 2] += e.rad[2] * omega * w6[face];
      }
    }
  }
  // OPEN probes: the outdoor sky, untouched
  for (let p = 0; p < P; p++) if (state[p] === ProbeState.Open) for (let face = 0; face < 6; face++) data[(face * P + p) * 4 + 3] = 1;

  // ── 3. one bounce (pass 2): each rock hit re-radiates what pass 1 left at it ──
  if (bounce && TR > 0) {
    const pass1 = data.slice();
    dilate(pass1, state, dims, 3);
    const s4 = [0, 0, 0, 0];
    for (let ti = 0; ti < TR; ti++) {
      const p = tracedList[ti];
      if (state[p] === ProbeState.Inactive) continue;
      const addT = [0, 0, 0, 0, 0, 0], addR = [0, 0, 0, 0, 0, 0], addG = [0, 0, 0, 0, 0, 0], addB = [0, 0, 0, 0, 0, 0];
      for (let r = 0; r < rays; r++) {
        const ri = ti * rays + r;
        if (rayKind[ri] !== RAY_ROCK) continue;
        const o = ri * 6;
        sampleField({ origin, spacing, dims, data: pass1 }, rayHit[o], rayHit[o + 1], rayHit[o + 2], rayHit[o + 3], rayHit[o + 4], rayHit[o + 5], NORMAL_BIAS, s4);
        const lT = rockAlbedo * s4[3];
        const k = rockAlbedo / Math.PI;
        const lr = k * s4[0], lg = k * s4[1], lb = k * s4[2];
        const dx = rayDir[ri * 3], dy = rayDir[ri * 3 + 1], dz = rayDir[ri * 3 + 2];
        const w6 = [dx > 0 ? dx : 0, dx < 0 ? -dx : 0, dy > 0 ? dy : 0, dy < 0 ? -dy : 0, dz > 0 ? dz : 0, dz < 0 ? -dz : 0];
        for (let face = 0; face < 6; face++) {
          const w = w6[face];
          if (w <= 0) continue;
          addT[face] += lT * w; addR[face] += lr * w; addG[face] += lg * w; addB[face] += lb * w;
        }
      }
      for (let face = 0; face < 6; face++) {
        const o = (face * P + p) * 4;
        data[o] += addR[face] * wE; data[o + 1] += addG[face] * wE; data[o + 2] += addB[face] * wE;
        data[o + 3] = Math.min(1, data[o + 3] + addT[face] * wT);
      }
    }
  }
  dilate(data, state, dims, 4);
  for (let p = 0; p < P; p++) {
    if (state[p] === ProbeState.Inactive) stats.inactive++;
    else if (state[p] === ProbeState.Open) stats.open++;
    else stats.traced++;
  }
  stats.ms = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
  return { version: CAVE_LIGHT_FIELD_VERSION, origin, spacing, dims, data, state, stats };
}

/** Where a ray leaves the occluder grid's box (for the far-ground march). */
function exitT(g: TriGrid, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): number {
  const tx = dx > 0 ? (g.x1 - ox) / dx : dx < 0 ? (g.x0 - ox) / dx : Infinity;
  const ty = dy > 0 ? (g.y1 - oy) / dy : dy < 0 ? (g.y0 - oy) / dy : Infinity;
  const tz = dz > 0 ? (g.z1 - oz) / dz : dz < 0 ? (g.z0 - oz) / dz : Infinity;
  return Math.min(tx, ty, tz);
}

/**
 * The ground around a cave as occluder triangles (normals UP, like the chunk mesh) over a box, `step` metres a cell;
 * `drop(a, b, c)` removes a triangle (the chunk assembler's cave-mouth cut). Heights come from `heightAt`.
 */
export function groundPatch(
  x0: number, z0: number, x1: number, z1: number, step: number,
  heightAt: (x: number, z: number) => number,
  drop?: (ax: number, ay: number, az: number, bx: number, by: number, bz: number, cx: number, cy: number, cz: number) => boolean,
): { positions: Float32Array; indices: Uint32Array } {
  const nx = Math.max(1, Math.ceil((x1 - x0) / step)), nz = Math.max(1, Math.ceil((z1 - z0) / step));
  const positions = new Float32Array((nx + 1) * (nz + 1) * 3);
  for (let j = 0; j <= nz; j++) for (let i = 0; i <= nx; i++) {
    const x = x0 + i * step, z = z0 + j * step, v = (j * (nx + 1) + i) * 3;
    positions[v] = x; positions[v + 1] = heightAt(x, z); positions[v + 2] = z;
  }
  const idx: number[] = [];
  const P = (i: number, j: number): number => j * (nx + 1) + i;
  const add = (a: number, b: number, c: number): void => {
    if (drop && drop(positions[a * 3], positions[a * 3 + 1], positions[a * 3 + 2], positions[b * 3], positions[b * 3 + 1], positions[b * 3 + 2], positions[c * 3], positions[c * 3 + 1], positions[c * 3 + 2])) return;
    idx.push(a, b, c);
  };
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const A = P(i, j), B = P(i + 1, j), C = P(i, j + 1), D = P(i + 1, j + 1);
    add(A, C, B); add(B, C, D); // counter-clockwise from above: the normal faces up
  }
  return { positions, indices: new Uint32Array(idx) };
}

/** Concatenate occluder meshes, tagging each triangle with its kind (0 rock, 1 ground). */
export function mergeOccluders(parts: Array<{ positions: Float32Array; indices: Uint32Array; kind: 0 | 1 }>): { positions: Float32Array; indices: Uint32Array; triKind: Uint8Array } {
  let nv = 0, ni = 0;
  for (const p of parts) { nv += p.positions.length; ni += p.indices.length; }
  const positions = new Float32Array(nv), indices = new Uint32Array(ni), triKind = new Uint8Array(ni / 3);
  let vo = 0, io = 0;
  for (const p of parts) {
    positions.set(p.positions, vo);
    const base = vo / 3;
    for (let i = 0; i < p.indices.length; i++) indices[io + i] = p.indices[i] + base;
    triKind.fill(p.kind, io / 3, (io + p.indices.length) / 3);
    vo += p.positions.length; io += p.indices.length;
  }
  return { positions, indices, triKind };
}
